/**
 * Dashboard "Site deliveries" (17 #8): Site DNs are tracked from Dispatched (left the store) to Received
 * (the site signed for them). The card counts this week's DNs and the ones still waiting — amber once a DN
 * is unconfirmed 48 hours after dispatch (once IT schedules the hourly job, it also reminds the project
 * engineer) — and opens a DN or its "Confirm receipt" sheet. It replaces the old "Pending DNs" count, which
 * called every DN older than 48 h pending although a recorded DN had already left the store.
 * Everything counts delivery notes, not ledger documents (a DN topped up in a second session is one DN),
 * and a DN reversed before the site signed is left out: nothing of it is on its way.
 */
import { useMemo } from 'react';
import { BellOff, CheckCircle2, ChevronRight, Clock, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { formatDate, formatDateTime, formatTimeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CardFoot, CardHead, InvCard, LinkButton } from './InventoryUi';
import { SITE_DN_OVERDUE_HOURS, documentLabel, groupMovements, plural, siteDeliveryNotes, siteDnReceipt, siteDnSentAt, timeOf } from './inventoryModel';

const WEEK_MS = 7 * 24 * 3600000;
const LIST_MAX = 4;

export default function SiteDeliveriesCard({ movements = [], projects = [], canEdit = true, now = Date.now(), reminders = null, onOpenDocument, onConfirm, onRecordSiteDn, onShowAll, className }) {
  const projectNames = useMemo(() => new Map(projects.map((project) => [project.id, project.name])), [projects]);
  // One entry per delivery note; `siteDns` = the ones not reversed (what the counts are about).
  const allDns = useMemo(() => siteDeliveryNotes(movements), [movements]);
  const siteDns = useMemo(() => allDns.filter((dn) => !dn.reversed), [allDns]);
  const tracked = useMemo(
    () => siteDns.map((group) => ({ group, receipt: siteDnReceipt(group, now) })).filter((entry) => entry.receipt),
    [siteDns, now]
  );
  const thisWeek = siteDns.filter((dn) => now - timeOf(siteDnSentAt(dn)) <= WEEK_MS).length;
  const waiting = tracked.filter((entry) => entry.receipt.state !== 'received');
  const overdue = waiting.filter((entry) => entry.receipt.state === 'overdue');
  const lastReceived = tracked
    .filter((entry) => entry.receipt.state === 'received')
    .sort((left, right) => timeOf(right.receipt.receivedAt) - timeOf(left.receipt.receivedAt))[0];
  const firstReceipt = useMemo(() => {
    const receipts = groupMovements(movements).filter((group) => group.kind === 'GRN');
    return receipts.length ? receipts[receipts.length - 1].date : '';
  }, [movements]);

  const badge = overdue.length
    ? <StatusPill tone="amber" icon={Clock} label={`${overdue.length} not confirmed`} data-testid="site-dns-overdue" />
    : waiting.length
      ? <StatusPill tone="blue" label={`${waiting.length} on the way`} />
      : tracked.length
        ? <StatusPill tone="green" icon={CheckCircle2} label="All confirmed" />
        : null;

  return (
    <InvCard className={cn('flex flex-col', className)} data-testid="inventory-site-deliveries">
      <CardHead icon={Truck} title="Site deliveries" right={badge} />
      <dl className="grid grid-cols-3 divide-x divide-line-2 border-b border-line-2">
        {[
          ['This week', thisWeek, 'site-dns-week'],
          ['Waiting for site', waiting.length, 'site-dns-waiting'],
          [`Over ${SITE_DN_OVERDUE_HOURS} h`, overdue.length, 'site-dns-late']
        ].map(([label, value, testId], index) => (
          <div key={label} className="px-4 py-3">
            <dt className="text-[11.5px] text-ink-3">{label}</dt>
            <dd className={cn('mt-0.5 font-heading text-[20px] font-black leading-none tabular-nums', index === 2 && value ? 'text-status-amber-ink' : 'text-ink')} data-testid={testId}>{value}</dd>
          </div>
        ))}
      </dl>
      {waiting.length ? (
        <ul className="divide-y divide-line-2" data-testid="site-dns-waiting-list">
          {waiting.slice(0, LIST_MAX).map(({ group, receipt }) => {
            const project = projectNames.get(group.projectId) || group.movements[0]?.project_name || 'Site';
            return (
              <li key={group.dnKey} className="flex items-center gap-3 px-5 py-2.5" data-testid={`site-dn-waiting-${receipt.dnId}`}>
                <button type="button" onClick={() => onOpenDocument?.(group.key)} className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <span className="block truncate text-[13.5px] text-ink"><b className="font-mono text-[12.5px] font-bold">{documentLabel(group)}</b> → {project}</span>
                  <span className={cn('block text-[12px]', receipt.state === 'overdue' ? 'font-semibold text-status-amber-ink' : 'text-ink-3')}>
                    Dispatched {formatTimeAgo(receipt.dispatchedAt)} · {receipt.state === 'overdue' ? 'not confirmed yet' : 'waiting for the site'}
                  </span>
                </button>
                {canEdit ? (
                  <Button type="button" size="xs" variant="outline" className="shrink-0 text-ink" onClick={() => onConfirm?.(group)} data-testid={`site-dn-confirm-${receipt.dnId}`}>
                    Confirm receipt
                  </Button>
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-4" aria-hidden="true" />
                )}
              </li>
            );
          })}
        </ul>
      ) : allDns.length ? (
        <p className="px-5 py-4 text-[13px] text-ink-2" data-testid="site-dns-all-received">
          {lastReceived ? (
            <>
              <CheckCircle2 className="mr-1.5 inline h-4 w-4 align-[-3px] text-status-green-ink" aria-hidden="true" />
              {documentLabel(lastReceived.group)} received by <b className="font-semibold text-ink">{lastReceived.receipt.receivedBy}</b> · {formatDateTime(lastReceived.receipt.receivedAt)}
            </>
          ) : siteDns.length
            ? 'Every Site DN recorded before tracking started counts as delivered.'
            : 'Every Site DN recorded so far was reversed — nothing is on its way.'}
        </p>
      ) : (
        <div className="px-5 py-4" data-testid="site-dns-empty">
          <p className="text-[13.5px] font-semibold text-ink">No Site DN recorded in the app yet</p>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            {firstReceipt ? `Nothing has left the stores in the app since the first receipt on ${formatDate(firstReceipt)}. ` : ''}
            A Site DN stays “on the way” until the site signs for it — from a phone, with a signature.
          </p>
          {canEdit ? (
            <Button type="button" size="sm" variant="outline" className="mt-2.5 text-ink" onClick={() => onRecordSiteDn?.()} data-testid="site-dns-record">
              <Truck aria-hidden="true" /> Record a Site DN
            </Button>
          ) : null}
        </div>
      )}
      {reminders && !reminders.scheduled ? (
        // Admins only (GET /api/jobs): the 48 h reminder is built but runs only once IT schedules the hourly job.
        <p className="mt-auto flex items-start gap-1.5 border-t border-line-2 px-5 py-2.5 text-[12px] text-ink-3" data-testid="site-dns-reminders-off">
          <BellOff className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>Reminders off — nobody is reminded of a DN after {SITE_DN_OVERDUE_HOURS} h until IT schedules the hourly reminder job.</span>
        </p>
      ) : null}
      {allDns.length ? (
        <CardFoot className={cn('justify-between', !(reminders && !reminders.scheduled) && 'mt-auto')}>
          <span>{plural(siteDns.length, 'Site DN')} recorded · {tracked.filter((entry) => entry.receipt.state === 'received').length} signed for on site</span>
          <LinkButton onClick={() => onShowAll?.()}>All Site DNs <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" /></LinkButton>
        </CardFoot>
      ) : null}
    </InvCard>
  );
}
